import { IFocusTrapZoneProps, IImageProps, IStackTokens } from '@fluentui/react';
import { IPDefaultButton, IPIconButton, IPImage, IPModal, IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { cancelIcon, globalStackTokensGapSmall } from 'globalStyles';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface IGenericModalProps {
  onClose: () => void;
  onSave?: () => void | Promise<void>;
  isActionPending?: boolean;
  isOpen: boolean;
  title: string;
  subTitle?: string;
  hideFooter?: boolean;
  keepOpenAfterSave?: boolean;
  saveButtonText?: string;
  saveDisabled?: boolean;
  closeButtonText?: string;
  children?: ReactNode;
  maxHeight?: string | number;
  minHeight?: string | number;
  maxWidth?: string | number;
  minWidth?: string | number;
  width?: string | number;
  height?: string | number;
  verticalGap?: IStackTokens;
  optionalButtons?: JSX.Element;
  footerContent?: ReactNode;
  growContent?: boolean;
  transition?: string;
  allowFocusOutsideTrap?: boolean;
  // Forwarded to the underlying Fluent Modal's FocusTrapZone. Use `{ disabled: true }` when the
  // modal hosts a component that manages its own focus and body-portaled popups (e.g. the Eigenpal
  // docx editor's Radix dropdowns), so Fluent's focus trap does not fight it.
  focusTrapZoneProps?: IFocusTrapZoneProps;
  isBlocking?: boolean;
  imageProps?: IImageProps;
  // Reserve scrollbar space so content width stays stable when inner sections expand/collapse.
  // Off by default to avoid a permanent gutter on the many modals that never scroll.
  stableScrollbarGutter?: boolean;
}

const GenericModal = (props: IGenericModalProps) => {
  const { t } = useTranslation(['translation']);

  return (
    <IPModal
      isBlocking={props.isBlocking ?? false}
      isOpen={props.isOpen}
      onDismiss={props.onClose}
      styles={{
        scrollableContent: {
          maxWidth: props.maxWidth ? props.maxWidth : 400,
          minWidth: props.minWidth ? props.minWidth : 300,
          minHeight: props.minHeight ? props.minHeight : undefined,
          maxHeight: props.maxHeight ? props.maxHeight : undefined,
          width: props.width ? props.width : undefined,
          height: props.height ? props.height : undefined,
          transition: props.transition,
          // Reserve scrollbar space so content width stays stable when accordions expand (opt-in).
          scrollbarGutter: props.stableScrollbarGutter ? 'stable' : undefined,
        },
      }}
      forceFocusInsideTrap={props.allowFocusOutsideTrap === undefined ? true : !props.allowFocusOutsideTrap}
      focusTrapZoneProps={props.focusTrapZoneProps}
    >
      <IPStack
        verticalFill
        tokens={props.verticalGap ? props.verticalGap : undefined}
        styles={{
          root: {
            paddingTop: 20,
            paddingLeft: 20,
            paddingRight: 20,
            paddingBottom: 10, //other 10 is part of the footer
          },
        }}
      >
        <IPStack.Item>
          <IPStack
            horizontal
            horizontalAlign={'space-between'}
            verticalAlign="start"
            tokens={globalStackTokensGapSmall}
          >
            <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
              {props.imageProps && <IPImage {...props.imageProps} />}
              <IPText variant="xLarge">{props.title}</IPText>
            </IPStack>
            <IPIconButton iconProps={cancelIcon} onClick={props.onClose} />
          </IPStack>
        </IPStack.Item>
        {props.subTitle && (
          <IPStack.Item styles={{ root: { paddingTop: 5 } }}>
            <IPText>{props.subTitle}</IPText>
          </IPStack.Item>
        )}
        <IPStack.Item grow={props.growContent ?? true}>{props.children}</IPStack.Item>
        {!props.hideFooter && (
          <IPStack.Item styles={{ root: { paddingTop: 10, paddingBottom: 10 } }}>
            <IPStack tokens={globalStackTokensGapSmall}>
              <IPStack.Item>
                <IPSeparator />
              </IPStack.Item>
              <IPStack.Item>
                {props.footerContent ?? (
                  <IPStack horizontal horizontalAlign={props.optionalButtons ? 'space-between' : 'end'}>
                    <IPStack.Item>{props.optionalButtons}</IPStack.Item>
                    <IPStack horizontal tokens={globalStackTokensGapSmall}>
                      <IPDefaultButton
                        disabled={props.saveDisabled || props.isActionPending}
                        primary={props.onSave !== undefined}
                        onClick={async () => {
                          if (props.onSave) {
                            await props.onSave();
                          }
                          if (!props.onSave || !props.keepOpenAfterSave) {
                            props.onClose();
                          }
                        }}
                        text={
                          props.onSave
                            ? props.saveButtonText
                              ? props.saveButtonText
                              : t('translation:General.Button.Save')
                            : props.closeButtonText
                            ? props.closeButtonText
                            : t('translation:General.Button.Close')
                        }
                      >
                        {props.onSave && props.isActionPending && (
                          <IPSpinner size={IPSpinnerSize.small} styles={{ root: { paddingLeft: 5 } }} />
                        )}
                      </IPDefaultButton>
                      {props.onSave && (
                        <IPDefaultButton
                          disabled={props.isActionPending}
                          onClick={() => {
                            props.onClose();
                          }}
                          text={props.closeButtonText ? props.closeButtonText : t('translation:General.Button.Cancel')}
                        ></IPDefaultButton>
                      )}
                    </IPStack>
                  </IPStack>
                )}
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
        )}
      </IPStack>
    </IPModal>
  );
};

export default GenericModal;
